import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { toggleFavorite, ResourceType } from "~/services/gateways/favorite.gateway";
import { createErrorHandler } from "~/helpers/errorHandling.helper";
import { useFavoritesStore } from "~/store/favoritesStore";

export interface ToggleFavoriteVariables {
    resource_type: ResourceType;
    resource_id: string;
    /** Current favorite state at click time (the value we are toggling away from). */
    is_favorited: boolean;
}

/**
 * Toggle favorite for a report or folder.
 *
 * State-management driven: the new favorite state is written to the
 * `favoritesStore` immediately (optimistic), so every consumer that reads via
 * `useFavoriteStatus` updates right away. We intentionally DO NOT invalidate or
 * refetch the report/folder list on toggle — re-querying the heavy, encrypted
 * list on every star click is expensive. The fresh list (with favorites sorted
 * first) is reconciled later, when a "suitable" action already re-queries it
 * (create/delete/move/rename, filter change, folder navigation), at which point
 * the overrides are cleared.
 */
export const useFavoriteCRUD = () => {
    const toggleFavoriteMutation = useMutation({
        mutationKey: ["toggleFavorite"],
        mutationFn: (vars: ToggleFavoriteVariables) =>
            toggleFavorite(vars.resource_type, vars.resource_id, vars.is_favorited),
        onMutate: (vars: ToggleFavoriteVariables) => {
            const { setOverride } = useFavoritesStore.getState().actions;
            // Snapshot the previous override so we can roll back on error.
            const previousOverride = useFavoritesStore.getState().overrides[vars.resource_id];
            // Optimistically reflect the toggled value immediately.
            setOverride(vars.resource_id, !vars.is_favorited);
            return { resourceId: vars.resource_id, previousOverride };
        },
        onError: (err, _vars, context) => {
            if (context) {
                const { setOverride, removeOverride } = useFavoritesStore.getState().actions;
                if (context.previousOverride === undefined) {
                    removeOverride(context.resourceId);
                } else {
                    setOverride(context.resourceId, context.previousOverride);
                }
            }
            createErrorHandler(
                "Não foi possível atualizar o favorito",
                "Erro ao favoritar"
            )(err);
        },
        onSuccess: (data) => {
            // Persist the authoritative server value as the override (no refetch).
            useFavoritesStore.getState().actions.setOverride(data.resource_id, data.is_favorited);
            toast.success(
                data.is_favorited
                    ? "Adicionado aos favoritos"
                    : "Removido dos favoritos"
            );
        },
    });

    return { toggleFavoriteMutation };
};
